/**
 * Alerts — what the insight engine raised, and why.
 *
 * Shapes follow the platform's `alert`, `rule` and `rule_evaluation` tables
 * (`ops.py` list, `insight.py` explain). The explanation is the screen the
 * platform is proudest of: the rule, its expression, every value it
 * resolved, the threshold it was compared against, and the reference it
 * cites. An alert nobody can interrogate gets ignored within a month.
 */

export const ALERT_KINDS = ["reactive", "predictive"] as const;
export type AlertKind = (typeof ALERT_KINDS)[number];

export const ALERT_STATUSES = ["open", "acknowledged", "resolved", "suppressed"] as const;
export type AlertStatus = (typeof ALERT_STATUSES)[number];

export interface Alert {
  id: string;
  title: string;
  kind: AlertKind;
  /** 1 (low) … 5 (critical), as the pack declared the rule. */
  severity: number;
  status: AlertStatus;
  raisedAt: string;
  /** Predictive only: when the fault is expected. */
  predictedFor?: string;
  /** Predictive only: 0–1. */
  confidence?: number;
  ruleKey?: string;
  assetTag?: string;
  zoneCode?: string;
  /** Where to draw it — the asset's, else the zone's, point — when the platform located it. */
  point?: { lon: number; lat: number };
}

export interface AlertExplanation extends Alert {
  occurrences?: number;
  /** Predictive only: confirmed by the work order completing, refuted by it being cancelled, null while open. */
  confirmed?: boolean | null;
  ruleName?: string;
  ruleClass?: string;
  /** The expression the rule evaluated. */
  expression?: string;
  /** The regulatory or engineering basis the rule cites. */
  reference?: string;
  /** Thresholds and other parameters the expression used. */
  parameters: Record<string, unknown>;
  /** Every function call the expression made, with the value it returned. */
  resolved: Record<string, unknown>;
  /** Rules carrying a regulatory threshold ship gated until verified. */
  verifyRequired?: boolean;
  subjectKind?: string;
  subjectLabel?: string;
  outcome?: string;
  resultingActions: ResultingAction[];
}

export interface ResultingAction {
  kind: "work_order";
  /** The work order number. */
  ref: string;
  title: string;
  status: string;
}

export const SEVERITY_LABEL: Record<number, string> = {
  1: "Low",
  2: "Minor",
  3: "Moderate",
  4: "Major",
  5: "Critical",
};

export function severityLabel(s: number): string {
  return SEVERITY_LABEL[s] ?? `Severity ${s}`;
}

/** Work-order priority an alert of this severity would ordinarily be raised at. */
export function priorityForSeverity(s: number): 1 | 2 | 3 | 4 | 5 {
  if (s >= 5) return 1;
  if (s === 4) return 2;
  if (s === 3) return 3;
  if (s === 2) return 4;
  return 5;
}

/** Open alerts, most severe then most recent first — the order a duty manager reads them in. */
export function sortAlerts(alerts: readonly Alert[]): Alert[] {
  return [...alerts].sort(
    (a, b) => b.severity - a.severity || new Date(b.raisedAt).getTime() - new Date(a.raisedAt).getTime(),
  );
}
